import { SportType } from 'modules/core/domain/enums/sport-type';
import { DataMapper } from 'modules/core/domain/interfaces/data-mapper';
import { normalizePhone } from 'shared/utils/normalize-phone';
import { DEFAULT_ORGANIZER_TIMEZONE } from '../constants/timezones';
import { LatestChampionship } from '../interfaces/latest-championship';
import { Organizer } from '../interfaces/organizer';
import { OrganizerInfoDto } from '../interfaces/organizer-dto';

export class OrganizerDataMapper implements DataMapper<Organizer, OrganizerInfoDto> {
  public decode(payload: OrganizerInfoDto): Organizer {
    const tournament = payload;
    // Backend already returns championships in the desired sorted order via championshipsPaginated
    const latestChampionships = tournament.championships.slice(0, 5).map(
      (item) =>
        ({
          id: item.id,
          name: item.name,
          startDate: item.startDate,
          endDate: item.endDate,
        }) as LatestChampionship,
    );

    const logo = tournament.images.find((image) => image.type === 'logo');

    return {
      id: tournament.id,
      name: tournament.name,
      // Бэкенд отдаёт вид спорта заглавными (`FOOTBALL`), а enum домена — строчный.
      // Без приведения значение расходится с собственным типом: у RTK Query
      // `football` и `FOOTBALL` дают разные ключи кэша, а в Supabase `sport_type`
      // копится вперемешку.
      sportType: String(tournament.sportType).toLowerCase() as SportType,
      logoUrl: logo?.externalUrl,
      email: tournament.email,
      phone: tournament.phone ? normalizePhone(tournament.phone) : undefined,
      description: tournament.description,
      latestChampionships,
      address: tournament.address,
      timezone: tournament.timezone || DEFAULT_ORGANIZER_TIMEZONE,
      adminUserId: tournament.adminUser?.id ?? null,
    } as Organizer;
  }

  public encode(payload: Organizer): OrganizerInfoDto {
    return {
      id: payload.id,
      name: payload.name,
      // Обратно — тем же регистром, в каком вид спорта объявлен в схеме.
      sportType: payload.sportType.toUpperCase() as SportType,
      email: payload.email,
      phone: payload.phone,
      description: payload.description,
      championships: payload.latestChampionships.map((item) => ({
        id: item.id,
        name: item.name,
        startDate: item.startDate,
        endDate: item.endDate,
      })),
      images: payload.logoUrl
        ? [
            {
              type: 'logo',
              externalUrl: payload.logoUrl,
            },
          ]
        : undefined,
      address: {
        city: payload.address?.city ?? undefined,
        country: payload.address?.country ?? undefined,
        region: payload.address?.region ?? undefined,
      },
      timezone: payload.timezone,
      adminUser: payload.adminUserId === null ? null : { id: payload.adminUserId },
    } as OrganizerInfoDto;
  }
}
